import { useEffect, useMemo, useState } from 'react';
import parseSiren from 'siren-parser';
import { DateTime } from 'luxon';
import { entityStore, sirenActions } from '~/utility/BinSentry-ui-utility';
import groupBy from 'lodash/groupBy';

// Find the set of bins from the orders, and map to sites
export function useOrderBarnsToSites(orders) {
  const [orderBinIdToSite, setOrderBinIdToSite] = useState(new Map());
  const barnIds = useMemo(() => orders.map(order => order.getLink('https://api.binsentry.com/rel/barn')?.href), [orders]);

  useEffect(() => {
    (async () => {
      let isMounted = true;
      let hasUnloadedBarns = false;
      const promises = Array.from(barnIds).map(async binId => {
        if (orderBinIdToSite.has(binId)) {
          return [binId, orderBinIdToSite.get(binId)];
        }
        hasUnloadedBarns = true;
        const site = await siteFromBarn(binId);
        return [binId, site];
      });
      const entries = await Promise.all(promises);
      if (hasUnloadedBarns && isMounted) {
        setOrderBinIdToSite(new Map(entries));
      }
      return () => {
        isMounted = false;
      };
    })();
  }, [barnIds, orderBinIdToSite]);

  return orderBinIdToSite;
}

/**
 * Asynchronously fetches the site (farm) that a particular bin is on
 * @param {string} barnId
 * @return {Promise<Entity> | undefined} The site (farm) that the bin is on
 */
export async function siteFromBarn(barnId) {
  try {
    const barnEntity = parseSiren((await entityStore.fetchWait(barnId, false)).entity);
    if (!barnEntity) {
      return;
    }
    const getAncestors = barnEntity.getAction('get-ancestors');
    const farmField = getAncestors.getField('startOrganizationType').value.find(({ title }) => title === 'farm');
    if (!farmField) {
      return;
    }
    const farmType = farmField.value;
    const { entity: ancestors } = await sirenActions.submitAction(getAncestors, { startOrganizationType: farmType, endOrganizationType: farmType });
    return ancestors.getSubEntity('item');
  } catch (e) {
    // Just return undefined if, for example, the bin was deleted
  }
}

/**
 * Takes the provided orders and the map of bin ids to sites and creates a structure that groups first by day, then site
 * @param {[Entity]} orders
 * @param {Map<binId, Site>} orderBarnIdToSiteMap A mapping of barn ids from the orders to their site
 * @return {[[string, Map<string, {Entity, [Order]}>]]}
 */
export function arrangeOrdersByDayThenSite(orders, orderBarnIdToSiteMap) {
  const orderToSite = order => orderBarnIdToSiteMap.get(order.getLink('https://api.binsentry.com/rel/barn')?.href);
  const byDay = groupBy(orders, order => DateTime.fromISO(order.properties.deliveryDate).toISODate());
  const result = Object.entries(byDay).map(([deliveryDay, ordersForDay]) => {
    const ordersBySite = groupBy(ordersForDay, order => orderToSite(order)?.getLink('self').href);
    return [
      deliveryDay,
      new Map(Object.entries(ordersBySite).map(([siteId, orders]) => ([siteId, { site: orderToSite(orders[0]), orders }]))),
    ];
  });
  return result;
}

export function isOrderLate(orderEntity) {
  if (!orderEntity) {
    return undefined;
  }

  return !!orderEntity
    .getSubEntitiesByRel('https://api.binsentry.com/rel/flag')
    .find(flag => flag.properties.name === 'late' && flag.properties.visibility === 'visible');
}
